import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService as ToasterService } from 'ngx-toastr';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Pagination } from '@sd-portal/shared/models';
import { untilViewDestroyed } from '@sd-portal/shared/rxjs';

import { AuditLogLite } from '../+models/audit-log-lite.model';
import { UsersAuditUser } from '../+models/users-audit.model';
import { UsersAuditService } from '../users-audit.service';

type UserAuditLogView = 'user' | 'role';

@Component({
  selector: 'sd-portal-user-management-audit',
  templateUrl: './user-management-audit.component.html',
  styleUrls: ['./user-management-audit.component.scss']
})
export class UserManagementAuditComponent implements OnInit, OnDestroy {
  users: UsersAuditUser[] = [];
  usersLoading = false;
  usersPagination$ = new BehaviorSubject<Pagination>(new Pagination());

  selectedUser: UsersAuditUser | null = null;
  activeLogView: UserAuditLogView | null = null;
  logs: AuditLogLite[] = [];
  logsLoading = false;
  logsPagination$ = new BehaviorSubject<Pagination>(new Pagination());

  constructor(
    private readonly usersAuditService: UsersAuditService,
    private readonly toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.loadUsers(1, 10);
  }

  ngOnDestroy() {}

  loadUsers(pageNumber: number, pageSize: number) {
    this.usersLoading = true;
    this.usersAuditService
      .getUsers(pageNumber, pageSize)
      .pipe(
        untilViewDestroyed(this),
        catchError(() => {
          this.usersLoading = false;
          this.toasterService.error('Failed to retrieve users');
          return EMPTY;
        })
      )
      .subscribe(res => {
        this.usersLoading = false;
        this.users = res.resultSet || [];
        this.usersPagination$.next({
          ...new Pagination(),
          totalCount: res.totalCount,
          pageSize: res.pageSize,
          pageNumber: res.pageNumber
        });
      });
  }

  onUsersPageChange(pagination: Pagination) {
    this.loadUsers(pagination.pageNumber, pagination.pageSize);
  }

  selectUser(user: UsersAuditUser) {
    this.selectedUser = user;
    this.activeLogView = null;
    this.logs = [];
  }

  showUserLogs(user: UsersAuditUser) {
    this.selectedUser = user;
    this.activeLogView = 'user';
    this.logs = [];
    this.loadLogs(1, 10);
  }

  showRoleLogs(user: UsersAuditUser) {
    this.selectedUser = user;
    this.activeLogView = 'role';
    this.logs = [];
    this.loadLogs(1, 10);
  }

  loadLogs(pageNumber: number, pageSize: number) {
    if (!this.selectedUser || !this.activeLogView) {
      return;
    }
    this.logsLoading = true;
    const logs$ =
      this.activeLogView === 'user'
        ? this.usersAuditService.getUserLogs(this.selectedUser.id, pageNumber, pageSize)
        : this.usersAuditService.getUserRoleLogs(
            this.selectedUser.accountUserRoleId,
            pageNumber,
            pageSize
          );

    logs$
      .pipe(
        untilViewDestroyed(this),
        catchError(() => {
          this.logsLoading = false;
          this.toasterService.error('Failed to retrieve audit logs');
          return EMPTY;
        })
      )
      .subscribe(res => {
        this.logsLoading = false;
        this.logs = res.resultSet || [];
        this.logsPagination$.next({
          ...new Pagination(),
          totalCount: res.totalCount,
          pageSize: res.pageSize,
          pageNumber: res.pageNumber
        });
      });
  }

  onLogsPageChange(pagination: Pagination) {
    this.loadLogs(pagination.pageNumber, pagination.pageSize);
  }

  userTrackByFn(_index: number, user: UsersAuditUser) {
    return user.id;
  }

  logTrackByFn(_index: number, log: AuditLogLite) {
    return log.id;
  }
}
